import React, { useState, useEffect, useMemo } from "react";
import { useCorporateDetailsController } from "./CorporateDetailsController";
import { GoldenSourceHelper } from "../../app/Switch_Core_React_Standards";
import { CorporateHierarchyRules } from "./CorporateHierarchyRules";
import { useGraphController } from "../common/visuals/GraphController";
import ForceFieldSpheres from "../common/visuals/ForceFieldSpheres";

export default function ManageStructure({ initialSelectedIds = [], onClose }) {
  const { getRegistry, updateHierarchy, loading } = useCorporateDetailsController();
  const { nodes, edges, setGraphData } = useGraphController();

  const [registry, setRegistry] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [rootId, setRootId] = useState(null);
  const [draftHierarchy, setDraftHierarchy] = useState({});
  // 🔘 Track the discovered lineage branch
  const [branchIds, setBranchIds] = useState([]);

  useEffect(() => {
    const load = async () => {
      const data = await getRegistry();
      const validData = Array.isArray(data) ? data : [];
      setRegistry(validData);

      if (initialSelectedIds.length > 0) {
        const discovered = new Set();
        const existingLinks = {};

        // 🎯 Lineage Sweep Helper: Recursively find all connected relatives
        const findRelatives = (id) => {
          validData.forEach((item) => {
            const cId = GoldenSourceHelper.get(item, "CorporateEntityId");
            const pId = GoldenSourceHelper.get(item, "ParentEntityId");

            if (cId === id || pId === id) {
              if (!discovered.has(cId)) {
                discovered.add(cId);
                if (pId) existingLinks[cId] = pId;
                findRelatives(cId); // Recurse down
              }
              if (pId && !discovered.has(pId)) {
                discovered.add(pId);
                findRelatives(pId); // Recurse up
              }
            }
          });
        };

        initialSelectedIds.forEach((id) => findRelatives(id));
        
        const branchArray = Array.from(discovered);
        setBranchIds(branchArray);
        setDraftHierarchy(existingLinks);

        // 🎯 Auto-detect the highest node in the discovered branch to set as Root
        const rootNode = branchArray.find((id) => {
          const item = validData.find((r) => GoldenSourceHelper.get(r, "CorporateEntityId") === id);
          const pId = GoldenSourceHelper.get(item, "ParentEntityId");
          return !pId || !discovered.has(pId);
        });

        if (rootNode) setRootId(rootNode);
      }
    };
    load();
  }, [getRegistry, initialSelectedIds]);

  // 🔍 Updated Filter: Show the entire Branch context, not just selection
  const filteredList = useMemo(() => {
    if (!Array.isArray(registry)) return [];

    // Show the discovered branch if we have one, otherwise show everything
    const base = branchIds.length > 0
        ? registry.filter((c) => branchIds.includes(GoldenSourceHelper.get(c, "CorporateEntityId")))
        : registry;

    return base.filter((c) =>
      (GoldenSourceHelper.get(c, "EntityName") || "").toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [registry, branchIds, searchTerm]);

  /** 🪐 GRAPH MAPPING */
  useEffect(() => {
    if (registry.length === 0) return;

    const newNodes = filteredList.map((item) => ({
      id: String(GoldenSourceHelper.get(item, "CorporateEntityId")),
      label: GoldenSourceHelper.get(item, "EntityName"),
      type: "COMPANY",
    }));

    const newEdges = filteredList
      .map((item) => {
        const cId = GoldenSourceHelper.get(item, "CorporateEntityId");
        const pId = draftHierarchy[cId] ?? GoldenSourceHelper.get(item, "ParentEntityId");
        if (pId && pId !== cId) return { source: String(pId), target: String(cId) };
        return null;
      })
      .filter((edge) => edge !== null);

    setGraphData(newNodes, newEdges);
  }, [registry, filteredList, draftHierarchy, setGraphData]);

  const handleStageLink = (childId, parentId) => {
    setDraftHierarchy((prev) => ({ ...prev, [childId]: parentId || null }));
  };

  const handleSave = async () => {
    const changes = Object.entries(draftHierarchy);
    if (changes.length === 0) return onClose();
    try {
      const updatePromises = changes.map(([childId, parentId]) => {
        const originalEntity = registry.find(
          (r) => String(GoldenSourceHelper.get(r, "CorporateEntityId")) === childId
        );
        return updateHierarchy(originalEntity, parentId);
      });
      await Promise.all(updatePromises);
      onClose();
    } catch (err) {
      alert("Save failed: " + err.message);
    }
  };

  return (
    <div className="card shadow-sm border-0 p-4 animate__animated animate__fadeIn">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h5 className="fw-bold mb-0">Branch Hierarchy Editor</h5>
          <span className="extra-small text-muted text-uppercase fw-bold">Lineage Mode Active</span>
        </div>
        <div className="d-flex gap-2">
          <button className="btn btn-sm btn-outline-secondary extra-small fw-bold" onClick={onClose}>CANCEL</button>
          <button className="btn btn-sm btn-success extra-small fw-bold" onClick={handleSave} disabled={loading}>
            {loading ? "COMMITTING..." : "SAVE STRUCTURE"}
          </button>
        </div>
      </div>

      <input 
        type="text" className="form-control form-control-sm mb-4" 
        placeholder="Filter within this branch..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} 
      />

      <div className="row g-4">
        <div className="col-md-5 border-end">
          <h6 className="extra-small fw-bold text-muted mb-3">BRANCH ENTITIES</h6>
          <div className="list-group list-group-flush overflow-auto" style={{ maxHeight: "400px" }}>
            {filteredList.map((c) => {
              const id = GoldenSourceHelper.get(c, "CorporateEntityId");
              const isRootEligible = CorporateHierarchyRules.canBeRoot(id, registry, draftHierarchy);
              return (
                <div key={id} className={`list-group-item d-flex justify-content-between align-items-center px-2 ${rootId === id ? "bg-light" : ""}`}>
                  <span className={`small ${!isRootEligible && rootId !== id ? "text-muted" : "fw-bold"}`}>
                    {GoldenSourceHelper.get(c, "EntityName")}
                  </span>
                  <button 
                    className={`btn btn-xs ${rootId === id ? "btn-success" : "btn-outline-dark"} extra-small`}
                    disabled={!isRootEligible && rootId !== id} onClick={() => setRootId(id)}
                  >
                    {rootId === id ? "ROOT" : "SET ROOT"}
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        <div className="col-md-7 ps-4">
          <h6 className="extra-small fw-bold text-muted mb-3">PARENT-CHILD RELATIONSHIPS</h6>
          {rootId ? (
            <div className="tree-builder">
              <div className="p-2 bg-dark text-white rounded mb-3 small fw-bold">
                <i className="bi bi-building-fill me-2"></i>
                {GoldenSourceHelper.get(registry.find((r) => GoldenSourceHelper.get(r, "CorporateEntityId") === rootId), "EntityName")}
              </div>
              <div className="ms-3 border-start ps-3 overflow-auto" style={{ maxHeight: "300px" }}>
                {filteredList.filter((c) => GoldenSourceHelper.get(c, "CorporateEntityId") !== rootId).map((child) => {
                  const childId = GoldenSourceHelper.get(child, "CorporateEntityId");
                  const currentParent = draftHierarchy[childId] ?? GoldenSourceHelper.get(child, "ParentEntityId");
                  return (
                    <div key={childId} className="mb-2 p-2 bg-white border-bottom d-flex align-items-center">
                      <div className="small me-auto fw-bold">{GoldenSourceHelper.get(child, "EntityName")}</div>
                      <select 
                        className="form-select form-select-sm w-50 extra-small"
                        value={currentParent || ""}
                        onChange={(e) => handleStageLink(childId, e.target.value)}
                      >
                        <option value="">No Parent</option>
                        <option value={rootId}>[Root] {GoldenSourceHelper.get(registry.find(r => GoldenSourceHelper.get(r, "CorporateEntityId") === rootId), "EntityName")}</option>
                        {filteredList.filter((p) => {
                          const pId = GoldenSourceHelper.get(p, "CorporateEntityId");
                          return pId !== childId && !CorporateHierarchyRules.wouldCreateCycle(childId, pId, registry, draftHierarchy);
                        }).map((p) => (
                          <option key={GoldenSourceHelper.get(p, "CorporateEntityId")} value={GoldenSourceHelper.get(p, "CorporateEntityId")}>
                            {GoldenSourceHelper.get(p, "EntityName")}
                          </option>
                        ))}
                      </select>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="text-center p-5 border rounded bg-light text-muted small italic">
              Select a ROOT node on the left to begin branch editing.
            </div>
          )}
        </div>
      </div>

      <hr className="my-5" />
      <div className="graph-container">
        <h6 className="extra-small fw-bold text-muted mb-3 text-uppercase">
          <i className="bi bi-globe me-2"></i>Force Field Sphere Visualization
        </h6>
        <ForceFieldSpheres nodes={nodes} edges={edges} height={400} />
      </div>
    </div>
  );
}